import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { CdkDragHandle } from '@angular/cdk/drag-drop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import type { StopCardHeader } from './route.types';

let nextBodyId = 0;

/**
 * One stop of a route as an accordion card (`props.stopStyle: 'card'`). Presentational: the header
 * text comes in as `card`, the open/locked state as inputs, every action goes out as an output.
 * The stop's fields are projected as the body; a child marked `ddStopFooter` as the footer's left side.
 */
@Component({
  selector: 'dd-stop-card',
  template: `
    <ng-template #headContent>
      <span class="dd-stop-card__text">
        <span class="dd-stop-card__heading">
          <span class="dd-stop-card__title">{{ card().title }}</span>
          @if (card().incomplete) {
            <span class="dd-stop-card__badge" data-cy="route-stop-incomplete">Incompleet</span>
          }
        </span>
        <span class="dd-stop-card__line" data-cy="route-stop-line">
          @for (part of lineParts(); track $index) {
            @if ($index > 0) {
              <span class="dd-stop-card__dot" aria-hidden="true">{{ ' • ' }}</span>
            }
            {{ part }}
          }
        </span>
      </span>
      <span class="dd-stop-card__chevron">
        <mat-icon class="icon-size-5">{{ open() ? 'expand_less' : 'expand_more' }}</mat-icon>
      </span>
    </ng-template>

    <div
      class="dd-stop-card"
      [class.dd-stop-card--open]="open()"
      [class.dd-stop-card--last]="last() && railed()"
      data-cy="route-stop-card">
      <div class="dd-stop-card__top">
        <!-- Small muted icons left of the title, so the chevron is the only prominent control. -->
        @if (showOrder() && inlineOrder()) {
          <div class="dd-stop-card__order" data-cy="route-stop-order">
            @if (canMoveUp()) {
              <button
                type="button"
                mat-icon-button
                class="dd-stop-card__order-button"
                (click)="moveUp.emit()"
                [attr.aria-label]="'Stop ' + (position() + 1) + ' omhoog verplaatsen'"
                matTooltip="Omhoog">
                <mat-icon class="icon-size-4" svgIcon="heroicons_outline:arrow-up"></mat-icon>
              </button>
            }
            @if (dragHandle()) {
              <mat-icon
                class="dd-stop-card__drag icon-size-4 cursor-move"
                cdkDragHandle
                matTooltip="Slepen"
                svgIcon="mat_outline:drag_indicator"></mat-icon>
            }
            @if (canMoveDown()) {
              <button
                type="button"
                mat-icon-button
                class="dd-stop-card__order-button"
                (click)="moveDown.emit()"
                [attr.aria-label]="'Stop ' + (position() + 1) + ' omlaag verplaatsen'"
                matTooltip="Omlaag">
                <mat-icon class="icon-size-4" svgIcon="heroicons_outline:arrow-down"></mat-icon>
              </button>
            }
          </div>
        }
        <!-- Read mode opens too: the note, contact and volumes are only readable in the body. -->
        <button
          type="button"
          class="dd-stop-card__header"
          [attr.aria-expanded]="open()"
          [attr.aria-controls]="bodyId"
          (click)="toggleOpen.emit()">
          <ng-container *ngTemplateOutlet="headContent" />
        </button>
      </div>
      <!-- Hidden, not removed: the stop's fields keep their validators and watchers. -->
      <div class="dd-stop-card__body" [id]="bodyId" [class.hidden]="!open()">
        <ng-content />
      </div>
      @if (open() && !locked() && (hasFooterField() || removable())) {
        <div class="dd-stop-card__footer" data-cy="route-stop-footer">
          <div class="min-w-0 flex-1"><ng-content select="[ddStopFooter]" /></div>
          @if (removable()) {
            <button
              type="button"
              mat-icon-button
              class="dd-stop-card__remove"
              [attr.aria-label]="'Stop ' + (position() + 1) + ' verwijderen'"
              matTooltip="Stop verwijderen"
              (click)="remove.emit()">
              <mat-icon class="dd-stop-card__remove-icon" svgIcon="heroicons_outline:trash"></mat-icon>
            </button>
          }
        </div>
      }
    </div>
  `,
  styles: `
    :host {
      display: block;
    }

    /* stopStyle 'card'. Fuse tokens and --dd-badge-* switch with the light/dark class on html/body. */
    .dd-stop-card {
      margin-bottom: var(--dd-stop-gap, 28px);
      /* --fuse-border is the same pale grey as the slot cards and vanishes on the white card; the separator token shows in both schemes. */
      border: 1px solid var(--dd-separator);
      border-radius: 8px;
      background: var(--fuse-bg-card);
    }

    /* The open card carries the accent (Figma: pink in light, blue in dark): --dd-badge-accent-border flips with the scheme. */
    .dd-stop-card--open {
      border-color: var(--dd-badge-accent-border);
    }

    .dd-stop-card__top {
      display: flex;
      align-items: flex-start;
    }

    .dd-stop-card__header {
      display: flex;
      flex: 1 1 auto;
      min-width: 0;
      align-items: flex-start;
      gap: 0.75rem;
      /* Figma: 16 above the title row, 16 below the meta line; with the 1px divider the header is 77px. */
      padding: 16px;
      text-align: left;
      cursor: pointer;
      /* The global button hover/focus fill is a heavy slab and skips the order controls beside the button. */
      background: transparent !important;
    }

    /* One subtle tint over the whole top row (order controls included); the focus ring stays separate. */
    .dd-stop-card__top:has(.dd-stop-card__header:hover) {
      background: color-mix(in srgb, currentColor 4%, transparent);
      border-radius: 7px 7px 0 0;
    }

    .dd-stop-card__header:focus-visible,
    .dd-stop-card__remove:focus-visible {
      outline: 2px solid var(--dd-focus-ring) !important;
      outline-offset: 2px;
    }

    .dd-stop-card__text {
      display: flex;
      flex: 1 1 auto;
      min-width: 0;
      flex-direction: column;
      gap: 6px;
    }

    .dd-stop-card__heading {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 4px 8px;
    }

    .dd-stop-card__title {
      font-weight: 500;
      line-height: 20px;
    }

    .dd-stop-card__badge {
      padding: 4px 8px;
      border-radius: 8px;
      font-size: 12px;
      font-weight: 500;
      line-height: 1;
      background: var(--dd-badge-warning-bg);
      color: var(--dd-badge-warning-fg);
    }

    /* Figma: 36px icon button with a 20px glyph, so its centre lines up with the footer trash. The negative margin keeps it from growing the header. */
    .dd-stop-card__chevron {
      display: flex;
      flex: 0 0 36px;
      align-items: center;
      justify-content: center;
      width: 36px;
      height: 36px;
      margin: -8px 0;
      color: var(--fuse-text-default);
    }

    .dd-stop-card__line {
      font-size: 0.75rem;
      line-height: 18px;
      color: var(--fuse-text-secondary);
    }

    /* Figma 4px dot separator. The "•" stays in the DOM text (copy, tests, screen readers skip it) but is not drawn. */
    .dd-stop-card__dot {
      display: inline-block;
      width: 4px;
      height: 4px;
      margin: 0 6px;
      vertical-align: middle;
      border-radius: 9999px;
      background: var(--dd-separator);
      font-size: 0;
    }

    /* Small muted reorder icons left of the title (16px, secondary text colour). */
    .dd-stop-card__order {
      display: flex;
      flex: 0 0 auto;
      align-items: center;
      padding: 0.75rem 0 0 0.5rem;
    }

    .dd-stop-card__order-button {
      width: 24px !important;
      height: 24px !important;
      min-width: 0 !important;
      min-height: 0 !important;
      padding: 0 !important;
    }

    .dd-stop-card__order-button .mat-icon,
    .dd-stop-card__drag {
      color: var(--fuse-text-secondary);
    }

    /* The last card needs a wider gap: the "+" pill is centred in it and must clear the destination flag. */
    .dd-stop-card--last {
      margin-bottom: var(--dd-stop-last-gap, 36px);
    }

    .dd-stop-card__body.hidden {
      display: none;
    }

    /* Thin divider under the header, only while the body shows. */
    /* Figma: #C7C7CC, inset 8px left and 16px right (drawn as a pseudo-element so the fields keep the full 16px padding); 16px to the first label (the label box starts 13px below the field's own top). */
    .dd-stop-card__body {
      position: relative;
      padding: 3px 1rem 0;
    }

    .dd-stop-card__body::before {
      content: '';
      position: absolute;
      top: 0;
      left: 8px;
      right: 16px;
      height: 1px;
      background: var(--dd-separator);
    }

    /* The route point pads itself 12px top and bottom for the row layout; inside a card the body and footer own the spacing. */
    .dd-stop-card__body ::ng-deep .route-point {
      padding-bottom: 0;
    }

    /* Figma: 20px under the last field, 1px #EEEFF1 divider, then 16px, a 36px row with checkbox and trash centred on it. */
    .dd-stop-card__footer {
      position: relative;
      display: flex;
      align-items: center;
      gap: 0.75rem;
      margin-top: 20px;
      padding: 17px 16px 16px;
    }

    /* The divider is inset 16px each side (a pseudo-element, so the row keeps its own padding). */
    .dd-stop-card__footer::before {
      content: '';
      position: absolute;
      top: 0;
      left: 16px;
      right: 16px;
      height: 1px;
      background: var(--fuse-border, #eeeff1);
    }

    .dd-stop-card__footer > div {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 0.75rem;
      min-height: 36px;
    }

    .dd-stop-card__footer ::ng-deep .mdc-form-field {
      display: flex;
      align-items: center;
    }

    /* A footer toggle (toggleStyle 'checkbox') reads as Figma: 16px box, radius 2, 14px Medium label. Scoped to this footer. */
    /* !important: the tailwind utility is important:true, a plain override loses and leaves the box 16px above the trash's centre. */
    .dd-stop-card__footer ::ng-deep .mb-4 {
      margin-bottom: 0 !important;
    }

    /* Block-level, not inline: the line box around an inline-flex checkbox adds ~4px and lifts the box off the row's centre. */
    .dd-stop-card__footer ::ng-deep .mb-4,
    .dd-stop-card__footer ::ng-deep .mat-mdc-checkbox {
      display: flex;
    }

    .dd-stop-card__footer ::ng-deep .mdc-checkbox {
      width: 16px;
      height: 16px;
      padding: 0;
      margin: 0 10px 0 0;
      flex: 0 0 16px;
    }

    .dd-stop-card__footer ::ng-deep .mdc-checkbox__background {
      top: 0;
      left: 0;
      width: 16px;
      height: 16px;
      border-radius: 2px;
      border-width: 1px;
    }

    /* Figma: 1px --dd-border-secondary on white (tokens flip in dark) until the box is checked. */
    .dd-stop-card__footer ::ng-deep .mat-mdc-checkbox:not(.mat-mdc-checkbox-checked) .mdc-checkbox__background {
      border-color: var(--dd-border-secondary) !important;
      background: var(--fuse-bg-card);
    }

    .dd-stop-card__footer ::ng-deep .mdc-checkbox .mdc-checkbox__native-control {
      top: -10px;
      left: -10px;
    }

    .dd-stop-card__footer ::ng-deep .mdc-form-field > label {
      padding-left: 0;
      font-size: 14px;
      font-weight: 500;
      line-height: 1.3;
    }

    /* Destructive icon: --dd-badge-negative-fg has light and dark values. Figma: 20px glyph in a 36px button. */
    .dd-stop-card__remove {
      width: 36px !important;
      height: 36px !important;
      min-height: 36px !important;
      max-height: 36px !important;
      padding: 8px !important;
      color: var(--dd-badge-negative-fg);
    }

    .dd-stop-card__remove:hover {
      background: var(--dd-badge-negative-bg);
    }

    .dd-stop-card__remove-icon {
      width: 20px !important;
      min-width: 20px !important;
      min-height: 20px !important;
      height: 20px !important;
      color: var(--dd-badge-negative-fg);
    }
  `,
  imports: [NgTemplateOutlet, CdkDragHandle, MatButtonModule, MatIconModule, MatTooltipModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StopCardComponent {
  /** Header text and incomplete flag (see buildStopCardHeader) */
  readonly card = input.required<StopCardHeader>();
  /** The body shows; false = one summary line */
  readonly open = input(false);
  /** Read mode: no footer (the header still opens the read-only body) */
  readonly locked = input(false);
  /** The card sits in a route section's rail: the last card leaves room for the rail's "+" pill */
  readonly railed = input(false);
  readonly last = input(false);
  /** Zero-based position, for the per-stop accessible names */
  readonly position = input(0);
  readonly showOrder = input(false);
  /** Figma shows no arrows or drag handle left of the title; the route section reorders through its marker. Opt in to render them. */
  readonly inlineOrder = input(false);
  readonly canMoveUp = input(false);
  readonly canMoveDown = input(false);
  readonly dragHandle = input(false);
  readonly removable = input(false);
  /** A `ddStopFooter` child is projected */
  readonly hasFooterField = input(false);

  readonly toggleOpen = output();
  readonly moveUp = output();
  readonly moveDown = output();
  readonly remove = output();

  /** The header line as parts, so the separators can be drawn as dots. */
  protected readonly lineParts = computed(() =>
    (this.open() ? this.card().subline : this.card().collapsedLine).split(' • ')
  );

  protected readonly bodyId = `dd-stop-card-body-${++nextBodyId}`;
}
